import React, { memo, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import Logo from "../Logo";
import RegisterModal from "../Auth/RegisterModal";
import Marquee from "../Marquee";
import { useAuth } from "../../contexts/JWTContext";
import performLogout from "../../utils/performLogout";
import styles from "./Header.module.css";

const NAV = [
  { to: "/home", label: "หน้าแรก", icon: "/lux/icons/home.webp" },
  { to: "promo", label: "โปรโมชั่น", icon: "/lux/icons/gift.webp" },
  { to: "/reviews", label: "รีวิว", icon: "/lux/icons/star.webp" },
];

const formatMoney = (v) =>
  Number(v || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const Header = memo(({ onLoginClick, onContactClick }) => {
  const [isRegisterOpen, setRegisterOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();

  const currentUser = useSelector((s) => s.user.currentUser);
  const singleUsers = useSelector((s) => s.user.singleUsers);
  const playerBalance = useSelector((s) => s.user.playerBalance);
  const wheelPoint = useSelector((s) => s.user.wheelPoint);

  const account = useMemo(() => {
    if (Array.isArray(singleUsers)) return singleUsers[0] || null;
    if (Array.isArray(singleUsers?.data)) return singleUsers.data[0] || null;
    return null;
  }, [singleUsers]);

  const balance =
    account?.creditWallet ??
    (Array.isArray(playerBalance) ? playerBalance[0] : playerBalance) ??
    0;

  const displayName =
    currentUser?.username || currentUser?.name || account?.username || "สมาชิก";

  // allow other components (Home hero etc.) to open register modal
  useEffect(() => {
    const open = () => setRegisterOpen(true);
    window.addEventListener("open-register-modal", open);
    return () => window.removeEventListener("open-register-modal", open);
  }, []);

  // close user menu on outside click / route change
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(() => setMenuOpen(false), [location.pathname, location.search]);

  const closeRegister = useCallback(() => setRegisterOpen(false), []);
  const go = useCallback((path) => navigate(path), [navigate]);

  const promoPath = isAuthenticated ? "/menu?tab=autoPromo" : "/promotion";
  const isPromoActive =
    location.pathname === "/promotion" ||
    (location.pathname === "/menu" &&
      new URLSearchParams(location.search).get("tab") === "autoPromo");

  const isNavActive = (item) => {
    if (item.to === "promo") return isPromoActive;
    return location.pathname === item.to;
  };

  const renderNavItem = (item, variant) => {
    const to = item.to === "promo" ? promoPath : item.to;
    const active = isNavActive(item);
    return (
      <Link
        key={item.label}
        to={to}
        className={`${variant === "chip" ? styles.chip : styles.navLink} ${
          active ? styles.active : ""
        }`}
        aria-current={active ? "page" : undefined}
      >
        <img src={item.icon} alt="" aria-hidden="true" className={styles.navIcon} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <header className={styles.header}>
      {/* ---------------- main bar ---------------- */}
      <div className={styles.bar}>
        <div className={`lux-container ${styles.inner}`}>
          <button
            type="button"
            className={styles.logoBtn}
            onClick={() => go("/home")}
            aria-label="กลับหน้าแรก"
          >
            <Logo />
          </button>

          <nav className={styles.nav} aria-label="เมนูหลัก">
            {NAV.map((item) => renderNavItem(item, "link"))}
          </nav>

          <div className={styles.actions}>
            {!currentUser ? (
              <>
                <button
                  type="button"
                  className={`lux-btn lux-btn--ghost ${styles.authBtn}`}
                  onClick={onLoginClick}
                >
                  <Icon icon="solar:login-3-bold-duotone" width={18} />
                  <span>เข้าสู่ระบบ</span>
                </button>
                <button
                  type="button"
                  className={`lux-btn lux-btn--gold ${styles.authBtn}`}
                  onClick={() => setRegisterOpen(true)}
                >
                  <Icon icon="solar:user-plus-bold" width={18} />
                  <span>สมัครสมาชิก</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.wallet}
                  onClick={() => go("/menu")}
                  title="กระเป๋าเงิน"
                >
                  <span className={styles.walletIcon}>
                    <Icon icon="solar:wallet-money-bold" width={18} />
                  </span>
                  <span className={styles.walletText}>
                    <small>เครดิต</small>
                    <strong>฿ {formatMoney(balance)}</strong>
                  </span>
                </button>

                <button
                  type="button"
                  className={`lux-btn lux-btn--gold lux-btn--sm ${styles.depositBtn}`}
                  onClick={() => go("/menu?tab=deposit")}
                >
                  <Icon icon="solar:add-circle-bold" width={18} />
                  <span>ฝากเงิน</span>
                </button>

                <div className={styles.userWrap} ref={menuRef}>
                  <button
                    type="button"
                    className={styles.avatar}
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label="เมนูผู้ใช้"
                  >
                    <Icon icon="solar:user-rounded-bold-duotone" width={22} />
                  </button>

                  {menuOpen && (
                    <div className={styles.dropdown} role="menu">
                      <div className={styles.ddHead}>
                        <div className={styles.ddName}>{displayName}</div>
                        <div className={styles.ddStats}>
                          <span>
                            <Icon icon="solar:wallet-money-bold-duotone" width={14} />
                            ฿ {formatMoney(balance)}
                          </span>
                          <span>
                            <Icon icon="ri:copper-diamond-fill" width={14} />
                            {Number(wheelPoint || 0).toLocaleString()} เพชร
                          </span>
                        </div>
                      </div>
                      <button role="menuitem" onClick={() => go("/menu")}>
                        <Icon icon="solar:user-id-bold-duotone" width={18} /> บัญชีของฉัน
                      </button>
                      <button role="menuitem" onClick={() => go("/menu?tab=deposit")}>
                        <Icon icon="solar:card-recive-bold-duotone" width={18} /> ฝากเงิน
                      </button>
                      <button role="menuitem" onClick={() => go("/menu?tab=withdraw")}>
                        <Icon icon="solar:card-send-bold-duotone" width={18} /> ถอนเงิน
                      </button>
                      <button role="menuitem" onClick={() => go("/menu?tab=history")}>
                        <Icon icon="solar:history-bold-duotone" width={18} /> ประวัติธุรกรรม
                      </button>
                      <button role="menuitem" onClick={() => go("/menu?tab=autoPromo")}>
                        <Icon icon="solar:gift-bold-duotone" width={18} /> โปรโมชั่น
                      </button>
                      <hr className="lux-divider" />
                      <button
                        role="menuitem"
                        className={styles.ddDanger}
                        onClick={() => performLogout(logout)}
                      >
                        <Icon icon="solar:logout-2-bold-duotone" width={18} /> ออกจากระบบ
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- announce strip ---------------- */}
      <div className={styles.announce}>
        <div className={`lux-container ${styles.announceInner}`}>
          <span className={styles.announceTag}>
            <Icon icon="solar:volume-loud-bold" width={16} />
            <span>ประกาศ</span>
          </span>
          <div className={styles.announceMarquee}>
            <Marquee variant="header" />
          </div>
          {onContactClick && (
            <button type="button" className={styles.announceContact} onClick={onContactClick}>
              <Icon icon="solar:chat-round-call-bold-duotone" width={16} />
              <span>ติดต่อ 24 ชม.</span>
            </button>
          )}
        </div>
      </div>

      {/* ---------------- mobile category chips ---------------- */}
      {location.pathname !== "/menu" && (
        <nav className={`lux-scroll-x ${styles.chips}`} aria-label="เมนูหลัก">
          {NAV.map((item) => renderNavItem(item, "chip"))}
        </nav>
      )}

      {isRegisterOpen && (
        <RegisterModal
          onClose={closeRegister}
          onSwitchToLogin={() => {
            closeRegister();
            onLoginClick && onLoginClick();
          }}
        />
      )}
    </header>
  );
});

Header.displayName = "Header";

export default Header;
